import { Box, Divider, Paper, Typography } from '@mui/material'
import React from 'react'

interface DisqualifiersSectionProps {
  targetVersion: number
  startDate?: string
}

const DisqualifiersSection: React.FC<DisqualifiersSectionProps> = ({
  targetVersion,
  startDate = '',
}) => {
  // why: Large Meat Variance methodology changed on 2026-04-04 (ENG-1007, ENG-4520)
  // Use date-based check rather than targetVersion since this change is specific to Q2 FQ2 2026
  const useNewNotesText = startDate && startDate >= '2026-04-04'
  return (
    <Box sx={{ mt: 6, mb: 4 }}>
      <Typography variant="h6" sx={{ mb: 2, fontFamily: 'Lexend-SemiBold' }}>
        Disqualifiers
      </Typography>
      <Paper elevation={2} sx={{ p: 3 }}>
        <Typography
          variant="subtitle1"
          sx={{ fontFamily: 'Lexend-SemiBold', mt: 2 }}
        >
          Prime Costs
        </Typography>
        {useNewNotesText ? (
          <>
            <Typography
              variant="body1"
              sx={{ mb: 1, fontFamily: 'Lexend-Regular' }}
            >
              Large Meat Variance ≤ 0.25% → Earn Large Meat component (50% of
              Prime Cost bonus)
            </Typography>
            <Typography
              variant="body1"
              sx={{ mb: 1, fontFamily: 'Lexend-Regular' }}
            >
              Large Meat Variance &gt; 0.25% → Miss Large Meat component
            </Typography>
            <Typography
              variant="body1"
              sx={{ mb: 1, fontFamily: 'Lexend-Regular' }}
            >
              Labor Cost to Plan ≤ 0.25% above plan → Earn Labor component (50%
              of Prime Cost bonus)
            </Typography>
            <Typography
              variant="body1"
              sx={{ mb: 1, fontFamily: 'Lexend-Regular' }}
            >
              Labor &gt; 0.25% above plan → Miss Labor component
            </Typography>
            <Typography
              variant="body1"
              sx={{ mb: 2, fontFamily: 'Lexend-Regular' }}
            >
              Both components are independent — one cannot offset the other
            </Typography>
          </>
        ) : (
          <>
            <Typography
              variant="body1"
              sx={{ mb: 2, fontFamily: 'Lexend-Regular' }}
            >
              &lt; {targetVersion === 0 ? '1.5%' : '1%'} Full Prime Cost Bonus
            </Typography>
            <Typography
              variant="body1"
              sx={{ mb: 2, fontFamily: 'Lexend-Regular' }}
            >
              &gt; {targetVersion === 0 ? '1.5%' : '1%'}, &lt;{' '}
              {targetVersion === 0 ? '2%' : '1.5%'} Miss Prime Cost Bonus
            </Typography>
            <Typography
              variant="body1"
              sx={{ mb: 2, fontFamily: 'Lexend-Regular' }}
            >
              &gt; {targetVersion === 0 ? '2%' : '1.5%'} Disqualified From All
              Bonus
            </Typography>
          </>
        )}

        <Divider sx={{ my: 2 }} />

        <Typography
          variant="subtitle1"
          sx={{ fontFamily: 'Lexend-SemiBold', mt: 2 }}
        >
          Guest Metrics
        </Typography>
        {useNewNotesText ? (
          <>
            <Typography
              variant="body1"
              sx={{ mb: 1, fontFamily: 'Lexend-Regular' }}
            >
              OSAT (25% of Guest bonus): ≥ 80% → 25% | 75–79% → 18.75% | 70–74%
              → 12.5% | &lt; 70% → 0% (disqualified from ALL bonus)
            </Typography>
            <Typography
              variant="body1"
              sx={{ mb: 1, fontFamily: 'Lexend-Regular' }}
            >
              Google Review (25% of Guest bonus): ≥ 4.3 → 25% | 4.0–4.2 → 18.75%
              | 3.7–3.9 → 12.5% | 3.4–3.6 → 6.25% | &lt; 3.4 → 0%
            </Typography>
            <Typography
              variant="body1"
              sx={{ mb: 1, fontFamily: 'Lexend-Regular' }}
            >
              DoorDash (25% of Guest bonus): ≥ 4.5 &amp; &lt;1.1% avoidable
              cancellation → 25% | 4.0–4.4 → 18.75% | 3.5–3.9 → 12.5% | 3.0–3.4
              → 6.25% | &lt; 3.0 → 0%
            </Typography>
            <Typography
              variant="body1"
              sx={{ mb: 1, fontFamily: 'Lexend-Regular' }}
            >
              SOS (25% of Guest bonus): average of HME% and Qualtrics
              Drive-Thru% ≥ 70% → Earn; &lt; 70% → Miss. (Store 1682 exception:
              uses Qualtrics Speed of Service for both)
            </Typography>
            <Typography
              variant="body2"
              sx={{
                fontStyle: 'italic',
                color: 'text.secondary',
                fontFamily: 'Lexend-Regular',
              }}
            >
              *All four Guest Metric components are evaluated independently of
              each other and of Prime Cost components.*
            </Typography>
          </>
        ) : (
          <Typography
            variant="body1"
            sx={{ mb: 2, fontFamily: 'Lexend-Regular' }}
          >
            &lt; {targetVersion === 0 ? '60%' : '70%'} OSAT Disqualified From
            All Bonus
          </Typography>
        )}

        <Divider sx={{ my: 2 }} />

        <Typography
          variant="subtitle1"
          sx={{ fontFamily: 'Lexend-SemiBold', mt: 2 }}
        >
          NSF
        </Typography>
        <Typography
          variant="body1"
          sx={{ mb: 2, fontFamily: 'Lexend-Regular' }}
        >
          &lt; 90% all bonus disqualified
        </Typography>

        <Divider sx={{ my: 2 }} />

        <Typography
          variant="subtitle1"
          sx={{ fontFamily: 'Lexend-SemiBold', mt: 2 }}
        >
          Health Inspection
        </Typography>
        <Typography
          variant="body1"
          sx={{ mb: 2, fontFamily: 'Lexend-Regular' }}
        >
          &lt; 90% all bonus disqualified
        </Typography>

        <Typography
          variant="body2"
          sx={{
            fontStyle: 'italic',
            mt: 3,
            color: 'text.secondary',
            fontFamily: 'Lexend-Regular',
          }}
        >
          Note: NSF and Health Inspections Are Not Tracked In Expo
        </Typography>
      </Paper>
    </Box>
  )
}

export default DisqualifiersSection
